// src/hooks/useResizableSidebar.ts
import { useState, useEffect, useCallback } from 'react';

interface UseResizableSidebarProps {
  initialWidth: number;
  minWidth?: number;
  maxWidth?: number;
}

const useResizableSidebar = ({
  initialWidth,
  minWidth = 100,
  maxWidth = 500,
}: UseResizableSidebarProps) => {
  const [sidebarWidth, setSidebarWidth] = useState<number>(initialWidth);
  const [isResizing, setIsResizing] = useState<boolean>(false);

  const handleMouseDown = (e: React.MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = useCallback((e: MouseEvent): void => {
    if (!isResizing) return;
    e.stopPropagation();
    e.preventDefault();
    const newWidth = e.clientX;
    // Set min and max width constraints
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setSidebarWidth(newWidth);
    }
  }, [isResizing, maxWidth, minWidth])

  const handleMouseUp = (): void => {
    setIsResizing(false);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, isResizing]);

  return {
    sidebarWidth,
    isResizing,
    handleMouseDown,
  };
};

export default useResizableSidebar;
